import {
  IOrderHead,
  IResponse,
  IOrdersResponse,
  IOrderStyle,
  IObjectDataResponse,
  IMutationResponse,
  IOrderheadInput,
  IYarnDetailsInput,
  IStandardResponse,
  TimelineData,
} from "../models/order";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { EExcelImportType, EOrderEndPoint, orderApi } from "../services/orderApi";
import {  EOrderStatus } from "../models/order";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { TABLE_NAME } from "../../../utils/constant";

import { TOrderItem } from "../../raw-material/models/rmm_models";
import { ICreateYarnComposition, ICreateYarnCount, IUpdateYarnComposition, IUpdateYarnCount, IYarnComposition, IYarnCount } from "../../user/model/gestion";
import { EProviderEndPoint } from "../../../services/providersApi";
import { useEffect, useRef } from "react";
import { toast, ToastOptions } from "react-toastify";

export const ORDERS_QUERY_KEY = "order/all";

export interface UseOrdersOptions {
  type: TOrderItem;
  status?: EOrderStatus[];
  page?: number;
  limit?: number;
  filters?: Record<string, any>;
  search?: Record<string, any>;
  sort?: string;
  key?: string;
  global_search?: string;
  queryOptions?: {
    onSuccess?: (data: IOrdersResponse) => void;
  };
}

type ProgressInterval = ReturnType<typeof setInterval>;

const clearProgressInterval = (intervalRef: React.MutableRefObject<ProgressInterval | null>) => {
  if (intervalRef.current) {
    clearInterval(intervalRef.current);
    intervalRef.current = null;
  }
};

const toastOptions = {
  loading: { autoClose: false, closeButton: false } as ToastOptions,
  success: { type: 'success', isLoading: false, autoClose: 5000, closeButton: true } as ToastOptions,
  error: { type: 'error', isLoading: false, autoClose: 3000, closeButton: true } as ToastOptions,
};

const showToast = (
  toastIdRef: React.MutableRefObject<React.ReactText | null>,
  message: string,
  options: ToastOptions = {}
) => {
  if (toastIdRef.current) {
    toast.update(toastIdRef.current, { render: message, ...options });
  } else {
    toastIdRef.current = toast.loading(message, { ...toastOptions.loading, ...options });
  }
};

const YARN_KEY = "yarncompos/yarn_composition";
const YARN_COUNT_KEY = "yarncount/yarn_count";

export const useOrdersSearch = (key: string) => {
  const query = useQuery<IStandardResponse<Array<IOrderHead>>, Error>({
    queryKey: [EOrderEndPoint.ORDER_HEAD_SEARCH, key],
    queryFn: () => orderApi.getOrderHeadSearch(key),
  });

  const { data } = query;

  return {
    ...query,
    data: data,
  };
};

export const useOrderClientByCode = (code: string) => {
  return useQuery({
    queryKey: [EOrderEndPoint.ORDER_CLIENTS, code],
    queryFn: async () => {
      const rows = await orderApi.getOrderClientsByCodes([code]);
      return rows.find((row) => row.order_code?.trim() === code) ?? null;
    },
    enabled: !!code,
  });
};

export const useOrdersSearchMultiple = (keys: number[] | string[] | undefined) => {
  return useQuery({
    queryKey: ["orderHeadSearchMultiple", keys],
    queryFn: async () => {
      if (!keys || keys.length === 0) return [];

      const results = await Promise.all(
        keys.map(async (key) => {
          const res = await orderApi.getOrderHeadSearch(key.toString());
          return res.data ?? [];
        })
      );

      return results.flat();
    },
    enabled: !!keys && keys.length > 0,
  });
};

export const useOrderHeadsByIds = (
  ids: number[] | string[] | undefined
) => {
  const normalizedIds = (ids ?? [])
    .map(Number)
    .filter((id) => Number.isInteger(id) && id > 0);

  return useQuery<IOrderHead[], Error>({
    queryKey: [EOrderEndPoint.ONE_ORDER_HEAD, "multiple", normalizedIds],
    queryFn: async () => {
      const responses = await Promise.all(
        normalizedIds.map((id) => orderApi.getOneOrderHead(id))
      );

      return responses.map((response) => response.data);
    },
    enabled: normalizedIds.length > 0,
  });
};

export const useOrderHead = ({
  type,
  page = 1,
  limit,
  search = {},
  sort = "",
  global_search = "",
  queryOptions = {},
}: UseOrdersOptions) => {
  const queryClient = useQueryClient();
  const { currentTable } = useSelector(
    (state: RootState) => state.currentTable
  );
  const previousData = queryClient.getQueryData<IResponse<IOrderHead>>([
    EOrderEndPoint.ORDER_HEAD,
    { type, page: page - 1, limit, search, sort, global_search },
  ]);

  const query = useQuery<IResponse<IOrderHead>, Error>({
    queryKey: [
      EOrderEndPoint.ORDER_HEAD,
      { type, page, limit, search, sort, global_search },
    ],
    queryFn: () =>
      orderApi.getOrderHead(type, page, limit, search, sort, global_search),
    placeholderData: previousData,
    staleTime: 5 * 60 * 1000,
    ...queryOptions,
    enabled: !!currentTable.length && currentTable === TABLE_NAME.orderSheet,
  });

  const { data } = query.data || {};

  return {
    ...query,
    data: data,
    pagination: {
      limit: data?.limit,
      page: data?.page,
      total: data?.total,
      totalPages: data?.totalPages,
    },
  };
};

export const useOrdersStyle = ({
  type,
  status,
  page = 1,
  limit,
  search = {},
  sort = "",
  global_search = "",
  queryOptions = {},
}: UseOrdersOptions) => {
  const queryClient = useQueryClient();
  const { currentTable } = useSelector(
    (state: RootState) => state.currentTable
  );
  const previousData = queryClient.getQueryData<IResponse<IOrderStyle>>([
    EOrderEndPoint.ORDER_STYLE,
    { type, status, page: page - 1, limit, search, global_search },
  ]);

  const query = useQuery<IResponse<IOrderStyle>, Error>({
    queryKey: [
      EOrderEndPoint.ORDER_STYLE,
      { type, status, page, limit, search, sort, global_search },
    ],
    queryFn: () =>
      orderApi.getOrdersStyle(type, page, limit, search, sort, global_search),
    placeholderData: previousData,
    staleTime: 5 * 60 * 1000,
    ...queryOptions,
    enabled:
      !!currentTable.length &&
      [TABLE_NAME.orderStyle, TABLE_NAME.yarnAllocation].includes(
        currentTable as TABLE_NAME,
      ),
  });

  const { data } = query.data || {};

  return {
    ...query,
    data: data,
    pagination: {
      limit: data?.limit,
      page: data?.page,
      total: data?.total,
      totalPages: data?.totalPages,
    },
  };
};

export const useOneOrderHead = (id: number) => {
  const query = useQuery<IObjectDataResponse<IOrderHead>, Error>({
    queryKey: [EOrderEndPoint.ONE_ORDER_HEAD, id],
    queryFn: () => orderApi.getOneOrderHead(id),
    enabled: !!id,
  });

  const { data } = query.data || {};

  return {
    ...query,
    data: data,
  };
};

export const useOneOrderStyle = (id: number) => {
  const query = useQuery<IObjectDataResponse<IOrderStyle>, Error>({
    queryKey: [EOrderEndPoint.ONE_ORDER_STYLE, id],
    queryFn: () => orderApi.getOneOrderStyle(id),
    enabled: id > 0,
  });

  const { data } = query.data || {};

  return {
    ...query,
    data,
  };
};

export const useGetTimeline=(order_code:string)=>{
    const query = useQuery<IObjectDataResponse<TimelineData>, Error>({
    queryKey: [EOrderEndPoint.ONE_ORDER_HEAD, order_code],
    queryFn: () => orderApi.GetOrderTimeline(order_code),
    enabled: !!order_code,
  });

  const { data } = query.data || {};

  return {
    ...query,
    data: data,
  };
}

// export const useGetOCPdf = () => {
//   return useMutation({
//     mutationFn: (id: number) => orderApi.getPDF_OC(id),
//   });
// };





export const useCreateOrderhead = () => {
  return useMutation<IMutationResponse, Error, IOrderheadInput>({
    mutationFn: (orderhead) => orderApi.createOrderhead(orderhead),
  });
};

export const useUpdateOrderhead = () => {
  return useMutation<
    IMutationResponse,
    Error,
    { id: number; orderhead: Partial<IOrderheadInput> }
  >({
    mutationFn: ({ id, orderhead }) => orderApi.updateOrderhead(id, orderhead),
  });
};

export const useReopenOrder=()=>{
   return useMutation<IMutationResponse, Error, { id: number }>({
    mutationFn: ({ id }) => orderApi.ReopenOrder(id),
  });
}
export const useConfirmOrder = () => {
  return useMutation<IMutationResponse, Error, { id: number }>({
    mutationFn: ({ id }) => orderApi.ConfirmOrder(id),
  });
};
export const useDeleteOrCancelOrder = () => {
  return useMutation<IMutationResponse, Error, { id: number; type?: string }>({
    mutationFn: ({ id, type }) => orderApi.DeleteOrder(id, type),
  });
};

export const useAddYarnDetails = () => {
  return useMutation<
    IMutationResponse,
    Error,
    { id: number; yarnDetails: IYarnDetailsInput }
  >({
    mutationFn: ({ id, yarnDetails }) =>
      orderApi.addYarnDetails(id, yarnDetails),
  });
};

export const useUpdateYarnDetails = () => {
  return useMutation<
    IMutationResponse,
    Error,
    { id: number; yarnDetails: Partial<IYarnDetailsInput> }
  >({
    mutationFn: ({ id, yarnDetails }) =>
      orderApi.updateYarnDetails(id, yarnDetails),
  });
};

export const useGetAllYarnCompositions = (enabled: boolean = true) => {
  const query = useQuery<IStandardResponse<IYarnComposition[]>, Error>({
    queryKey: [YARN_KEY],
    queryFn: () => orderApi.getAllYarnCompositions(),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
  const { data } = query.data || {};
  return { ...query, data };
};
 
export const useGetOneYarnComposition = (id: number, enabled: boolean = true) => {
  const query = useQuery<IStandardResponse<IYarnComposition>, Error>({
    queryKey: [YARN_KEY, id],
    queryFn: () => orderApi.getOneYarnComposition(id),
    enabled: !!id && enabled,
  });
  const { data } = query.data || {};
  return { ...query, data };
};
 
export const useCreateYarnComposition = () => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (body: ICreateYarnComposition) =>
      orderApi.createYarnComposition(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [YARN_KEY] });
      queryClient.invalidateQueries({ queryKey: [EProviderEndPoint.YARN_COMPOSITION] });
    },
  });
 
  const createYarn = async (
    body: ICreateYarnComposition,
    onSuccess?: (res: any) => void,
    onError?: (err: any) => void
  ) => {
    try {
      const result = await mutation.mutateAsync(body);
      onSuccess?.(result);
      return { success: true, data: result };
    } catch (err) {
      onError?.(err);
      return { success: false, error: err };
    }
  };
 
  return {
    createYarn,
    loading: mutation.isPending,
    error: mutation.error,
  };
};
 
export const useUpdateYarnComposition = () => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: ({ id, body }: { id: number; body: IUpdateYarnComposition }) =>
      orderApi.updateYarnComposition(id, body),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: [YARN_KEY] });
      queryClient.invalidateQueries({ queryKey: [YARN_KEY, id] });
      queryClient.invalidateQueries({ queryKey: [EProviderEndPoint.YARN_COMPOSITION] });
    },
  });
 
  const updateYarn = async (
    id: number,
    body: IUpdateYarnComposition,
    onSuccess?: (res: any) => void,
    onError?: (err: any) => void
  ) => {
    try {
      const result = await mutation.mutateAsync({ id, body });
      onSuccess?.(result);
      return { success: true, data: result };
    } catch (err) {
      onError?.(err);
      return { success: false, error: err };
    }
  };
 
  return {
    updateYarn,
    loading: mutation.isPending,
    error: mutation.error,
  };
};
 
export const useDeleteYarnComposition = () => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (id: number) => orderApi.deleteYarnComposition(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [YARN_KEY] });
      queryClient.invalidateQueries({ queryKey: [EProviderEndPoint.YARN_COMPOSITION] });
    },
  });
 
  const deleteYarn = async (
    id: number,
    onSuccess?: (res: any) => void,
    onError?: (err: any) => void
  ) => {
    try {
      const result = await mutation.mutateAsync(id);
      onSuccess?.(result);
      return { success: true, data: result };
    } catch (err) {
      onError?.(err);
      return { success: false, error: err };
    }
  };
 
  return {
    deleteYarn,
    loading: mutation.isPending,
    error: mutation.error,
  };
};

export const useGetAllYarnCounts = (enabled: boolean = true) => {
  const query = useQuery<IStandardResponse<IYarnComposition[]>, Error>({
    queryKey: [YARN_COUNT_KEY],
    queryFn: () => orderApi.getAllYarnCounts(),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
  const { data } = query.data || {};
  return { ...query, data };
};
 
export const useGetOneYarnCount = (id: number, enabled: boolean = true) => {
  const query = useQuery<IStandardResponse<IYarnCount>, Error>({
    queryKey: [YARN_COUNT_KEY, id],
    queryFn: () => orderApi.getOneYarnCount(id),
    enabled: !!id && enabled,
  });
  const { data } = query.data || {};
  return { ...query, data };
};
 
export const useCreateYarnCount = () => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (body: ICreateYarnCount) =>
      orderApi.createYarnCount(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [YARN_COUNT_KEY] });
      queryClient.invalidateQueries({ queryKey: [EProviderEndPoint.YARN_COUNT] });
    },
  });
 
  const createYarn = async (
    body: ICreateYarnCount,
    onSuccess?: (res: any) => void,
    onError?: (err: any) => void
  ) => {
    try {
      const result = await mutation.mutateAsync(body);
      onSuccess?.(result);
      return { success: true, data: result };
    } catch (err) {
      onError?.(err);
      throw err;
    }
  };
 
  return {
    createYarn,
    loading: mutation.isPending,
    error: mutation.error,
  };
};
 
export const useUpdateYarnCount = () => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: ({ id, body }: { id: number; body: IUpdateYarnCount }) =>
      orderApi.updateYarnCount(id, body),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: [YARN_COUNT_KEY] });
      queryClient.invalidateQueries({ queryKey: [YARN_COUNT_KEY, id] });
      queryClient.invalidateQueries({ queryKey: [EProviderEndPoint.YARN_COUNT] });
    },
  });
 
  const updateYarn = async (
    id: number,
    body: IUpdateYarnCount,
    onSuccess?: (res: any) => void,
    onError?: (err: any) => void
  ) => {
    try {
      const result = await mutation.mutateAsync({ id, body });
      onSuccess?.(result);
      return { success: true, data: result };
    } catch (err) {
      onError?.(err);
      return { success: false, error: err };
    }
  };
 
  return {
    updateYarn,
    loading: mutation.isPending,
    error: mutation.error,
  };
};
 
export const useDeleteYarnCount = () => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (id: number) => orderApi.deleteYarnCount(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [YARN_COUNT_KEY] });
      queryClient.invalidateQueries({ queryKey: [EProviderEndPoint.YARN_COUNT] });
    },
  });
 
  const deleteYarn = async (
    id: number,
    onSuccess?: (res: any) => void,
    onError?: (err: any) => void
  ) => {
    try {
      const result = await mutation.mutateAsync(id);
      onSuccess?.(result);
      return { success: true, data: result };
    } catch (err) {
      onError?.(err);
      return { success: false, error: err };
    }
  };
 
  return {
    deleteYarn,
    loading: mutation.isPending,
    error: mutation.error,
  };
};

export const useUploadExcelImport = () => {
  return useMutation({
    mutationFn: ({ type, data }: { type: EExcelImportType; data: FormData }) =>
      orderApi.uploadExcelImport(type, data),
  });
};

export const useImportExcelProgress = (
  type: EExcelImportType,
  jobId: string | null,
  isUploading: boolean,
  setIsUploading: (v: boolean) => void,
  refetch: () => void
) => {
  const toastId = useRef<React.ReactText | null>(null);
  const intervalRef = useRef<ProgressInterval | null>(null);
  const idleCountRef = useRef(0);
  const MAX_IDLE_POLLS = 20; // ~10s à 500ms

  useEffect(() => {
    if (!isUploading || !jobId) {
      clearProgressInterval(intervalRef);
      return;
    }

    let cancelled = false;
    idleCountRef.current = 0;

    const poll = async () => {
      try {
        const { data } = await orderApi.getExcelImportProgress(type, jobId);
        const { status = 'idle', total = 0, inserted = 0, skipped = 0, errorMessage } = data || {};

        if (cancelled) return;

        if (status === 'idle') {
          idleCountRef.current += 1;
          if (idleCountRef.current > MAX_IDLE_POLLS) {
            showToast(toastId, "Échec de l'import : le job n'a pas pu démarrer", {
              ...toastOptions.error,
              autoClose: 10000,
            });
            toastId.current = null;
            clearProgressInterval(intervalRef);
            setIsUploading(false);
            return;
          }
          showToast(toastId, `Préparation de l'import...`);
          return;
        }

        idleCountRef.current = 0;

        if (status === 'error') {
          showToast(toastId, errorMessage || 'Import échoué', {
            ...toastOptions.error,
            autoClose: 10000,
          });
          toastId.current = null;
          clearProgressInterval(intervalRef);
          setIsUploading(false);
          return;
        }

        const processed = inserted + skipped;
        const percentage = total > 0 ? Math.min(Math.round((processed / total) * 100), 100) : 0;
        showToast(toastId, `Loading... ${percentage}% (${processed} / ${total})`);

        if (status === 'success') {
          if (inserted === 0 && skipped > 0) {
            showToast(toastId, `Aucune ligne importée : ${skipped} doublon(s) déjà existant(s) en base`, { ...toastOptions.error, autoClose: 10000 });
          } else if (skipped > 0) {
            showToast(toastId, `Import terminé : ${inserted} ajoutée(s), ${skipped} ignorée(s) (doublons)`, toastOptions.success);
          } else {
            showToast(toastId, `Import completed (${inserted} / ${total})`, toastOptions.success);
          }
          toastId.current = null;
          clearProgressInterval(intervalRef);
          setIsUploading(false);
          refetch();
        }
      } catch (err) {
        console.error('Progress update error:', err);
        showToast(toastId, 'Error during import', { ...toastOptions.error, autoClose: false });
        toastId.current = null;
        clearProgressInterval(intervalRef);
        setIsUploading(false);
      }
    };

    poll();
    intervalRef.current = setInterval(poll, 500);

    return () => {
      cancelled = true;
      clearProgressInterval(intervalRef);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUploading, type, jobId]);

  return toastId;
};
